import markdoc from "@markdoc/markdoc";
import { Table, TaskList, Strikethrough } from '@lezer/markdown';
import { languages } from '@codemirror/language-data';

// Custom Markdoc transformer logic
markdoc.transformer.findSchema = (node, config) => {
  return node.tag
    ? config?.tags?.[node.tag] ?? config?.tags?.$$fallback
    : config?.nodes?.[node.type];
};

// Default Markdoc configuration
const defaultMarkdocConfig = {
  tags: {
    $$fallback: {
      transform(node, config) {
        const children = node.transformChildren(config);
        const className = "cm-markdoc-fallbackTag";
        return new markdoc.Tag("div", { class: className }, [
          new markdoc.Tag("div", { class: `${className}--name` }, [
            node?.tag ?? ""
          ]),
          new markdoc.Tag("div", { class: `${className}--inner` }, children)
        ]);
      }
    },
    callout: {
      transform(node, config) {
        const children = node.transformChildren(config);
        const kind = node.attributes.type === "warning" ? "warning" : "info";
        const icon = kind === "warning" ? "icon-exclamation" : "icon-info";
        const className = `cm-markdoc-callout cm-markdoc-callout--${kind}`;
        return new markdoc.Tag("div", { class: className }, [
          new markdoc.Tag("span", { class: `icon ${icon}` }),
          new markdoc.Tag("div", {}, children)
        ]);
      }
    }
  }
};

// Default Lezer configuration
const defaultLezerConfig = {
  codeLanguages: languages,
  extensions: [Table, TaskList, Strikethrough],
};

// Valid modes
const validModes = ['edit', 'show'];

// Validation function
function validateConfig(overrides) {
  const config = { ...overrides };

  // Validate mode
  if (config.mode && !validModes.includes(config.mode)) {
    console.warn(`Invalid mode: ${config.mode}. Defaulting to 'show'.`);
    config.mode = 'show';
  }

  // Ensure markdoc and lezer objects exist
  config.markdoc = config.markdoc || {};
  config.lezer = config.lezer || {};

  return config;
}

// Main configuration function
export default function getEditorConfig(overrides = {}) {
  const validatedConfig = validateConfig(overrides);

  return {
    markdoc: {
      ...defaultMarkdocConfig,
      ...validatedConfig.markdoc,
    },
    lezer: {
      ...defaultLezerConfig,
      extensions: [
        ...(defaultLezerConfig.extensions || []),
        ...(validatedConfig.lezer?.extensions || []),
      ],
    },
    mode: validatedConfig.mode || 'show',
  };
}
